/**
 * [[token]] substitution for form definitions.
 *
 * A form author can type [[some_token]] into any label or paragraph in the form
 * builder; the page that opens the form supplies the values. Shared by every
 * render path (CustomFormPage, HomePage) so a form opened outside the flow that
 * supplies the values blanks its tokens rather than showing raw [[...]] markers
 * to the debtor.
 */

/**
 * Tokens whose value is markup rather than plain text. These are substituted
 * as-is only into properties Form.io renders as HTML; anywhere else the markup
 * is flattened to text, because those properties are rendered escaped.
 */
export const HTML_VARIABLES = ['payment_schedule', 'schedule_summary'];

// 'content' and 'html' are rendered as markup by Form.io; the rest are rendered
// as text.
const HTML_PROPERTIES = ['content', 'html'];
const TEXT_PROPERTIES = ['label', 'defaultValue', 'description', 'legend'];

/**
 * Replace every [[token]] in a form definition, in place, walking the whole
 * component tree - panels, columns, table cells and wizard pages all nest their
 * children.
 *
 * @param components the definition's components array
 * @param variables token name => value; missing tokens resolve to an empty string
 */
export function applyFormVariables(components: any[], variables: { [key: string]: string }) {
  if (!Array.isArray(components)) {
    return;
  }
  components.forEach((component) => {
    if (!component) {
      return;
    }
    HTML_PROPERTIES.concat(TEXT_PROPERTIES).forEach((property) => {
      if (typeof component[property] === 'string') {
        component[property] = resolveTokens(
          component[property],
          variables,
          HTML_PROPERTIES.indexOf(property) !== -1
        );
      }
    });
    applyFormVariables(component.components, variables);
    if (Array.isArray(component.columns)) {
      component.columns.forEach((column) => applyFormVariables(column?.components, variables));
    }
    if (Array.isArray(component.rows)) {
      component.rows.forEach((row) => {
        if (Array.isArray(row)) {
          row.forEach((cell) => applyFormVariables(cell?.components, variables));
        }
      });
    }
  });
}

function resolveTokens(
  text: string,
  variables: { [key: string]: string },
  allowHtml: boolean
): string {
  if (text.indexOf('[[') === -1) {
    return text;
  }
  return text.replace(/\[\[\s*([a-zA-Z0-9_]+)\s*\]\]/g, (match, name) => {
    const value = variables ? variables[name] : undefined;
    if (value === undefined || value === null) {
      return '';
    }
    const resolved = String(value);
    if (allowHtml || HTML_VARIABLES.indexOf(name) === -1) {
      return resolved;
    }
    // A markup token used where markup is not rendered - flatten it so the
    // debtor reads the schedule rather than its tags.
    return resolved.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  });
}
